import { FC, Fragment, useRef, useState } from 'react';

import { LmSwitcher, LmSwitcherOption, LmText } from '@mes-ui/lemma';
import { Typography } from '@mui/material';
import { IMappedFinalGrades } from 'portfolio3/features/performance';

import useClientWidth from '../../../hooks/useClientWidth';
import { Tooltip } from '../../../ui-kit';
import LineChart, { IChartHorizontalLine } from '../../common/lineChart';
import LineChartAxis from '../../common/lineChart/lineChartAxis';
import Popper from '../../common/Popper';
import { SingleDictionarySelect } from '../../common/Select';
import { AVERAGE_SUBJECTS_VALUE, CHART_LABEL_COLORS, CHART_LINE_COLORS, MAX_CHECKBOX_VALUES } from './consts';
import { EducationLevel } from './educationLevels';
import SubjectCheckbox from './SubjectCheckbox';
import { filterPerformanceByEducationLevel, getAvailableSubjectNames } from './utils';

import './index.scss';

interface IEducationalLevelFilterOption {
  code: number;
  value: EducationLevel;
  isVisible: boolean;
}

interface IPerformanceChartProps {
  performance: IMappedFinalGrades[];
}

// На данный момент не используется и требует доработок
const PerformanceChart: FC<IPerformanceChartProps> = ({ performance }) => {
  const [checkboxPopperOpen, setCheckboxPopperOpen] = useState(false);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuIconRef = useRef(null);
  const clientWidth = useClientWidth();

  const isMobileView = clientWidth <= 800;

  /**
   * состояние компонента
   */
  // выбранное значение уровня образования
  const [educationLevel, setEducationLevel] = useState<EducationLevel>(EducationLevel.ANY);
  // выбранные значения в чекбоксах, массив строк
  const initialCheckboxValue = [AVERAGE_SUBJECTS_VALUE];
  const [checkboxValues, setCheckboxValues] = useState<string[]>(initialCheckboxValue);
  // маппинг значения чекбокса на цвет
  const initialColorMap = {
    [AVERAGE_SUBJECTS_VALUE]: {
      lineColor: CHART_LINE_COLORS[0],
      labelColor: CHART_LABEL_COLORS[0],
    },
  };
  const [colorMap, setColorMap] = useState<
    Record<
      string,
      {
        lineColor: string;
        labelColor: string;
      }
    >
  >(initialColorMap);

  /**
   * подготовка всех возможных вычисляемых значений
   */
  // фильтруем данные по educationLevel
  const filteredPerformance = filterPerformanceByEducationLevel(performance, educationLevel);
  // формируем плоский массив предметов
  const flatSubjects = filteredPerformance.flatMap((performance) =>
    performance.subjects.filter((subject) => subject.markValue && subject.markValue > 0),
  );
  // находим уникальные предметы по фильтрованному массиву + сортируем по алфавиту
  const availableSubjectNames = getAvailableSubjectNames(flatSubjects);
  // оставляем только те предметы, которые выбраны в чекбоксах, по ним будет формироваться график
  const chartSubjectNames = availableSubjectNames.filter((subjectName) => checkboxValues.includes(subjectName));
  // опции в чекбоксах
  const checkboxOpions = [AVERAGE_SUBJECTS_VALUE, ...availableSubjectNames];
  /**
   * формирование данных для графика
   */
  const chartData: IChartHorizontalLine[] = chartSubjectNames.map((subjectName): IChartHorizontalLine => {
    const values = filteredPerformance.map((yearData) => {
      const subject = yearData.subjects.find(
        (s) => s.subjectName === subjectName && !s.nonMarkValue && s.markValue && s.markValue > 0,
      );

      return {
        interval: yearData.year,
        value: subject?.markValue,
      };
    });

    return {
      name: subjectName,
      values,
      color: colorMap[subjectName].lineColor,
    };
  });

  if (checkboxValues.includes(AVERAGE_SUBJECTS_VALUE)) {
    chartData.unshift({
      name: AVERAGE_SUBJECTS_VALUE,
      values: filteredPerformance.map((performance): IChartHorizontalLine['values'][number] => {
        return {
          interval: performance.year,
          value: performance.averageGrade,
        };
      }),
      color: colorMap[AVERAGE_SUBJECTS_VALUE].lineColor,
    });
  }

  /**
   * функции для работы с чекбоксами
   */
  const getFirstAvailableColorFromPool = () => {
    // получаем список используемых в данный момент цветов
    const colorValues = Object.values(colorMap);
    // получаем список доступных цветов
    const lineColorPool = CHART_LINE_COLORS.filter((colorString) => {
      return !colorValues.map((color) => color.lineColor).includes(colorString);
    });
    const labelColorPool = CHART_LABEL_COLORS.filter((colorString) => {
      return !colorValues.map((color) => color.labelColor).includes(colorString);
    });

    return {
      lineColor: lineColorPool.length > 0 ? lineColorPool[0] : null,
      labelColor: labelColorPool.length > 0 ? labelColorPool[0] : null,
    };
  };

  const enableCheckboxValue = (value: string) => {
    const newValue = [...checkboxValues, value];
    setCheckboxValues(newValue);

    const { lineColor, labelColor } = getFirstAvailableColorFromPool();
    if (lineColor && labelColor) {
      setColorMap({
        ...colorMap,
        [value]: {
          labelColor,
          lineColor,
        },
      });
    }
  };

  const disableCheckboxValue = (value: string) => {
    const newValue = checkboxValues.filter((currentValue) => currentValue !== value);
    setCheckboxValues(newValue);

    const newColorMap = { ...colorMap };
    delete newColorMap[value];
    setColorMap(newColorMap);
  };

  const toggleCheckboxValue = (value: string) => {
    const hasValue = checkboxValues.includes(value);
    if (hasValue) {
      disableCheckboxValue(value);
    } else {
      enableCheckboxValue(value);
    }
  };

  const resetCheckboxValue = () => {
    setCheckboxValues(initialCheckboxValue);
    setColorMap(initialColorMap);
  };

  const getCheckboxValueColor = (value: string) => {
    const colorObject = colorMap[value];
    return {
      lineColor: colorObject?.lineColor ?? undefined,
      labelColor: colorObject?.labelColor ?? undefined,
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toggleCheckboxPopper = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    setCheckboxPopperOpen((prevstate) => !prevstate);
  };

  const isCheckboxLimitReached =
    checkboxValues.filter((value) => value !== AVERAGE_SUBJECTS_VALUE).length >= MAX_CHECKBOX_VALUES;

  /**
   * Фильтр уровень образования
   */
  const educationLevelSelectController = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleChange: (item: any) => {
      const educationLevel = educationLevelFilters.find((filter) => filter.code === item)?.value;
      setEducationLevel(educationLevel ?? EducationLevel.ANY);
    },
  };

  const hasEducationNOO = performance.some(
    (performanceObject) => performanceObject.educationLevel === EducationLevel.NOO,
  );
  const hasEducationOOO = performance.some(
    (performanceObject) => performanceObject.educationLevel === EducationLevel.OOO,
  );
  const hasEducationCOO = performance.some(
    (performanceObject) => performanceObject.educationLevel === EducationLevel.COO,
  );

  const educationLevelOptions: IEducationalLevelFilterOption[] = [
    {
      code: 1,
      value: EducationLevel.ANY,
      isVisible: true,
    },
    {
      code: 2,
      value: EducationLevel.NOO,
      isVisible: hasEducationNOO,
    },
    {
      code: 3,
      value: EducationLevel.OOO,
      isVisible: hasEducationOOO,
    },
    {
      code: 4,
      value: EducationLevel.COO,
      isVisible: hasEducationCOO,
    },
  ];

  const educationLevelFilters = educationLevelOptions.filter((filter) => filter.isVisible);
  const activeEducationalLevel = educationLevelFilters.find((filter) => filter.value === educationLevel)?.code;

  return (
    <div className="performance-chart">
      <div className="performance-chart__left-column">
        <Typography variant="Paragraph LG/Semi Bold" style={{ marginBottom: '12px' }}>
          Динамика итогового балла по учебным годам
        </Typography>
        <div className="performance-chart__row">
          {isMobileView ? (
            <div className="performance-chart__filter-select">
              <SingleDictionarySelect
                optionsClass="select__item--common--type-study"
                options={educationLevelFilters}
                placeholder=""
                selectController={educationLevelSelectController}
                value={activeEducationalLevel}
              />
            </div>
          ) : (
            <LmSwitcher
              value={educationLevel}
              className="performance-chart__filter"
              variant="gray"
              size="medium"
              onChange={(value) => {
                setEducationLevel(value as EducationLevel);
              }}
            >
              {educationLevelFilters.map((filter) => (
                <LmSwitcherOption key={filter.code} value={filter.value}>
                  {filter.value}
                </LmSwitcherOption>
              ))}
            </LmSwitcher>
          )}
          <div
            className={`performance-chart__checkbox-container ${
              checkboxPopperOpen ? 'performance-chart__checkbox-container--active' : ''
            }`}
            ref={menuButtonRef}
            onClick={toggleCheckboxPopper}
            role="button"
          >
            Отображается: {checkboxValues.length}
          </div>
        </div>
        <LineChartAxis xAxisLabel="учебный год">
          <LineChart incomingData={chartData} yAxisLabel="итоговый балл" />
        </LineChartAxis>
      </div>
      <div className="performance-chart__right-column">
        <div className="performance-chart__subjects-header">
          <LmText variant="lg" weight="semi-bold">
            Предметы
          </LmText>
          <LmText
            variant="lg"
            weight="semi-bold"
            color="note"
            className="performance-chart__clear-button"
            role="button"
            onClick={resetCheckboxValue}
          >
            Сбросить все
          </LmText>
        </div>
        <div className="performance-chart__subjects-container">
          {checkboxOpions.map((value) => {
            const isChecked = checkboxValues.includes(value);
            const { lineColor, labelColor } = getCheckboxValueColor(value);
            const isTooltipShown = isCheckboxLimitReached && value !== AVERAGE_SUBJECTS_VALUE && !isChecked;

            const checkboxElement = (
              <SubjectCheckbox
                value={value}
                isChecked={isChecked}
                isDisabled={isCheckboxLimitReached && value !== AVERAGE_SUBJECTS_VALUE}
                checkboxColor={lineColor}
                labelColor={labelColor}
                onToggle={() => toggleCheckboxValue(value)}
              />
            );

            return isTooltipShown ? (
              <Tooltip
                key={value}
                title="Одновременно можно выбрать не более 5-ти предметов"
                className="performance-chart__tooltip"
                arrow
                placement="left"
                width="190px"
              >
                {checkboxElement}
              </Tooltip>
            ) : (
              <Fragment key={value}>{checkboxElement}</Fragment>
            );
          })}
        </div>
      </div>
      <Popper
        open={checkboxPopperOpen}
        anchorEl={menuButtonRef.current}
        onClosePopperMenu={(event) => {
          if (event.target !== menuButtonRef.current && event.target !== menuIconRef.current)
            setCheckboxPopperOpen(false);
        }}
        placement="bottom-end"
        style={{ zIndex: 2 }}
      >
        <div className="performance-chart__checkbox-menu">
          {checkboxOpions.map((value) => {
            const isChecked = checkboxValues.includes(value);
            const { lineColor, labelColor } = getCheckboxValueColor(value);

            return (
              <SubjectCheckbox
                key={value}
                value={value}
                isChecked={isChecked}
                isDisabled={isCheckboxLimitReached && value !== AVERAGE_SUBJECTS_VALUE}
                checkboxColor={lineColor}
                labelColor={labelColor}
                onToggle={() => toggleCheckboxValue(value)}
              />
            );
          })}
        </div>
      </Popper>
    </div>
  );
};

export default PerformanceChart;
